import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SectionHeader } from '@/components/cccd/SectionHeader';
import { StatusPill } from '@/components/cccd/StatusPill';
import { cccdScoring, type CategoryLeaderboard as CategoryLeaderboardData } from '@/lib/api/cccdScoringClient';
import type { EventCategory } from '@/lib/api/cccdAdminClient';
import { LoadState } from "@/components/ui/LoadState"
import { readActiveCategoryId, rememberActiveCategoryId } from '@/lib/cccdActiveCategory';

// es-CO, máx. 2 decimales: las sumas de decimales llegaban como 166.64999999999998.
const formatScore = (n: number) => n.toLocaleString('es-CO', { maximumFractionDigits: 2 });

// Vista de ranking/clasificación por categoría (cccd-scoring-ux-redesign,
// spec cccd-scoring-leaderboard) — separada de la grilla de captura
// (ScoringResultsGrid, que ya no muestra posición) y separada del Podio
// (PodiumResultsSection, selección manual para becas/descuentos, sin
// relación automática con este ranking calculado).
export function ScoringLeaderboard({ categories }: { categories: EventCategory[] }) {
  const scopeId = categories[0]?.eventId ?? categories[0]?.regionalId;
  const [activeCategoryId, setActiveCategoryId] = useState(() => readActiveCategoryId(scopeId, categories));
  useEffect(() => {
    rememberActiveCategoryId(scopeId, activeCategoryId);
  }, [scopeId, activeCategoryId]);

  const { data: board, isLoading } = useQuery<CategoryLeaderboardData>({
    queryKey: ['category-leaderboard', activeCategoryId],
    queryFn: () => cccdScoring.getCategoryLeaderboard(activeCategoryId),
    enabled: !!activeCategoryId,
  });

  if (!categories.some((c) => c.id === activeCategoryId) && categories[0]) {
    setActiveCategoryId(categories[0].id);
  }

  const hasAnyResult = board?.teams.some((t) => t.scores.some((s) => s.rawValue != null)) ?? false;

  return (
    <SectionHeader
      title="Clasificación"
      subtitle="Ranking calculado a partir de los puntajes registrados — de solo lectura, no afecta el Podio"
      icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4Z" /><path d="M17 5h2a2 2 0 0 1 2 2 4 4 0 0 1-4 4M7 5H5a2 2 0 0 0-2 2 4 4 0 0 0 4 4" /></svg>}
    >
      {categories.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Este evento no tiene categorías configuradas todavía.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-1.5">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategoryId(cat.id)}
                className={`min-h-11 rounded-lg px-3.5 py-1.5 text-[13px] font-medium transition-colors sm:min-h-0 ${
                  activeCategoryId === cat.id
                    ? 'bg-brand-50 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-400'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-300'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {isLoading ? (
            <LoadState />
          ) : !board || board.teams.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
              Sin equipos en esta categoría.
            </p>
          ) : board.parameters.length === 0 ? (
            <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
              Esta categoría no tiene parámetros vinculados — configúralos en Editar categoría.
            </p>
          ) : !hasAnyResult ? (
            <p className="rounded-xl border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400 dark:border-gray-700">
              Aún no se han registrado resultados para esta categoría.
            </p>
          ) : (
            <div className="-mx-6 max-h-[65vh] overflow-auto custom-scrollbar">
              <table className="w-full min-w-max text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <th className="sticky left-0 top-0 z-30 w-16 border-r border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900">Pos.</th>
                    <th className="sticky left-16 top-0 z-30 border-r border-b border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900">Equipo</th>
                    {board.parameters.map((p) => (
                      <th key={p.id} className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 px-3 py-2.5 font-semibold normal-case dark:border-gray-800 dark:bg-gray-900">{p.name}</th>
                    ))}
                    <th className="sticky top-0 z-20 border-b border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-900">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {board.teams.map((team, index) => {
                    const hasResults = team.scores.some((s) => s.rawValue != null);
                    // Dos filas seguidas con la misma posición son un empate: se marca con "=".
                    const tied =
                      hasResults &&
                      team.position != null &&
                      (board.teams[index - 1]?.position === team.position || board.teams[index + 1]?.position === team.position);
                    return (
                    <tr key={team.teamId} className="group border-t border-gray-100 transition-colors hover:bg-gray-50/70 dark:border-gray-800 dark:hover:bg-white/[0.03]">
                      <td className="sticky left-0 z-10 w-16 whitespace-nowrap border-r border-gray-200 bg-white px-4 py-3 transition-colors group-hover:bg-gray-50/70 dark:border-gray-800 dark:bg-gray-900 dark:group-hover:bg-white/[0.03]">
                        {hasResults && team.position != null ? (
                          <span title={tied ? `Empate en la posición ${team.position}` : undefined}>
                            <StatusPill tone="brand" label={tied ? `=${team.position}` : `#${team.position}`} />
                          </span>
                        ) : (
                          <span aria-label="Sin posición" className="text-gray-500 dark:text-gray-400">—</span>
                        )}
                      </td>
                      <td className="sticky left-16 z-10 border-r border-gray-200 bg-white px-3 py-3 transition-colors group-hover:bg-gray-50/70 dark:border-gray-800 dark:bg-gray-900 dark:group-hover:bg-white/[0.03]">
                        <p className="text-[13px] font-medium text-gray-800 dark:text-white/90">{team.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{team.institution?.name ?? 'Sin institución'}</p>
                        {!hasResults && <p className="text-xs text-gray-600 dark:text-gray-300">Sin resultados</p>}
                      </td>
                      {board.parameters.map((p) => {
                        const score = team.scores.find((s) => s.parameterId === p.id);
                        return (
                          <td key={p.id} className="px-3 py-3 text-[13px] text-gray-600 dark:text-gray-400">
                            {score?.calculatedScore != null ? (
                              formatScore(score.calculatedScore)
                            ) : score?.rawValue != null ? (
                              // Hay valor capturado pero el puntaje no se pudo calcular (p. ej. faltan alturas de otros equipos).
                              <span title="Hay un valor capturado, pero el puntaje aún no se puede calcular (por ejemplo, faltan datos de otros equipos o el motor del equipo)." className="text-warning-700 dark:text-warning-300">
                                Sin calcular
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        );
                      })}
                      <td className="bg-brand-50/40 px-3 py-3 text-sm font-semibold text-brand-700 dark:bg-brand-500/[0.06] dark:text-brand-400">
                        {formatScore(team.totalScore)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </SectionHeader>
  );
}
